<!--
 * A Vue component for showing a geolocation button
-->
<template>
    <div>
        <a
            :disabled="hasError"
            @click="$_geolocationClick"
            :title="$t('showLocation')"
            class="geo-location-link"
        >
            <span class="geobutton">
                <span :class="geolocationClass" />
            </span>
        </a>
    </div>
</template>

<script>
    import { ZOOM_LEVEL_SCALE_750, GPS_MARKER_TYPE } from './../../../constants';
    import { DegreesToRadians } from '../../../services/utilities';
    import { mapGetters } from 'vuex';
    import { isMobileDevice } from './../../../services/utilities/devices';
    import { unByKey as OlUnByKey } from 'ol/Observable';

    const ERROR_CODES = {
        NO_ERROR: 0,
        PERMISSION_DENIED: 1,
        POSITION_UNAVAILABLE: 2,
        TIMEOUT: 3
    };

    const TRACK_STATES = {
        INITIAL: 0,
        TRACK: 1,
        ROTATE: 2
    };

    const TRACK_OPTIONS = {
        0: {
            // INITIAL
            // Initial state when starting NedGlobe Mobile/Web
            // Map is facing north
            // Map stays in place, position location marker moves and can therefore disappear from view
            // For non-iOS devices, there is immediate permission to request the device orientation and
            // the location marker will rotate in the viewing direction of the device.
            mapTracking: false,
            mapRotation: false,
            markerTracking: true,
            markerRotation: true, // (only if granted)
            zoomToLocation: false
        },
        1: {
            // TRACK
            // Map is facing north
            // For iOS devices, permission is requested for device orientation. The answer to this is stored per session.
            // If permission is given, the location marker will rotate in the viewing direction of the device.
            // Location marker is shown in the middle of the map and marker rotates
            // Map moves in the direction the device is traveling
            // When the map is panned/zoomed, it returns to the initial state
            mapTracking: true,
            mapRotation: false,
            markerTracking: true,
            markerRotation: true, // (only if granted)
            zoomToLocation: true // First time: Zoom in and go to location, second time: no zooming, only go to location
        },
        2: {
            // ROTATE
            // Map moves in the direction the device is traveling
            // Map rotates in the viewing direction of the device.
            // Location marker is displayed in the middle of the map and marker is pointing north
            // When the map is panned/zoomed, it returns to the initial state
            mapTracking: true,
            mapRotation: true,
            markerTracking: true,
            markerRotation: false,
            zoomToLocation: false
        }
    };

    export default {
        name: 'GeoLocationButton',
        inheritAttrs: false,
        data() {
            return {
                trackState: 0,
                errorCode: ERROR_CODES.NO_ERROR,
                messageHasBeenShown: false,
                hasDeviceOrientationPermission: false,

                // Event handlers
                errorHandler: null,
                changePositionHandler: null,
                pointerDragHandler: null,
                wheelHandler: null
            };
        },

        mounted() {
            this.$_addEventListeners();
            this.$_getOrientationPermission();
            this.gpsTracker?.setTracking(
                TRACK_OPTIONS[this.trackState].markerTracking
            );
            this.$store.commit('MAP_ADD_MARKER_GPS', { mapName: 'main' });
        },

        beforeDestroy() {
            this.$_removeEventListeners();
        },

        computed: {
            ...mapGetters(['getMainMap']),

            gpsTracker() {
                return this.getMainMap?.gpsTracker;
            },

            hasError() {
                return this.errorCode !== ERROR_CODES.NO_ERROR;
            },

            /**
             * Class for displaying image on button
             * @return {string} CSS class name
             */
            geolocationClass() {
                return {
                    map_controls_position: true,
                    'flex rotated_left':
                        this.trackState === TRACK_STATES.INITIAL && !this.hasError,
                    'fixed rotated_left':
                        this.trackState === TRACK_STATES.TRACK && !this.hasError,
                    'fixed_north rotated_right':
                        this.trackState === TRACK_STATES.ROTATE && !this.hasError,
                    'lost rotated_left': this.hasError
                };
            },

            /**
             * Show rotation marker only on mobile devices and when permission is granted
             */
            showRotationMarkerInMap() {
                return isMobileDevice() && this.hasDeviceOrientationPermission;
            }
        },

        watch: {
            showRotationMarkerInMap(newVal) {
                if (newVal) {
                    this.$store.commit('MARKER_GPS_CHANGE_ICON', {
                        mapName: 'main',
                        typeMarker: GPS_MARKER_TYPE.Direction
                    });
                } else {
                    this.$store.commit('MARKER_GPS_CHANGE_ICON', {
                        mapName: 'main',
                        typeMarker: GPS_MARKER_TYPE.Default
                    });
                }
            }
        },

        methods: {
            /**
             * For iOS 13+ ask for permission after a user click
             */
            requestOrientationPermission() {
                if (
                    window.DeviceOrientationEvent &&
                    !this.hasDeviceOrientationPermission
                ) {
                    if (
                        typeof DeviceOrientationEvent.requestPermission ===
                        'function'
                    ) {
                        DeviceOrientationEvent.requestPermission()
                            .then((permissionState) => {
                                if (permissionState == 'granted') {
                                    this.hasDeviceOrientationPermission = true;
                                    this.$_addDeviceOrientationEventListener();
                                }
                            })
                            .catch(() => {
                                // Do not handle error, location marker can't be rotated
                            });
                    }
                }
            },

            /**
             * Listen for the deviceorientation event and handle the raw data
             * Use deviceorientationabsolute event if available to get absolute orientation.
             */
            $_addDeviceOrientationEventListener() {
                const doEvent =
                    'ondeviceorientationabsolute' in window
                        ? 'deviceorientationabsolute'
                        : 'deviceorientation';

                window.addEventListener(
                    doEvent,
                    this.$_handleDeviceOrientation,
                    true
                );
            },

            $_addEventListeners() {
                // Error
                this.errorHandler = this.gpsTracker.on('error', (error) => {
                    this.gpsTracker?.setTracking(false);
                    this.errorCode = error.code;
                    this.$_showMessage();
                });

                // Track position: current position of the device reported in the current projection
                this.changePositionHandler = this.gpsTracker.on(
                    'change:position',
                    () => {
                        // No error
                        this.errorCode = ERROR_CODES.NO_ERROR;

                        // Change position
                        this.$_changePosition(false);
                    }
                );

                // If user moves the map (pan or zoom), then don't center the new position anymore
                this.pointerDragHandler = this.getMainMap.map.on(
                    'pointerdrag',
                    () => {
                        this.$_gotoInitialTrackState();
                    }
                );

                // If mouse wheel is rolled up or down (zoom in/out), then don't center the new position anymore
                this.wheelHandler = this.getMainMap.map.on('wheel', () => {
                    this.$_gotoInitialTrackState();
                });
            },

            /**
             * Handle regular non iOS 13+ devices
             * For iOS 13+ is asked for permission when geobutton is clicked
             */
            $_getOrientationPermission() {
                if (window.DeviceOrientationEvent) {
                    if (
                        typeof DeviceOrientationEvent.requestPermission ===
                        'function'
                    ) {
                        DeviceOrientationEvent.requestPermission()
                            .then((permissionState) => {
                                if (permissionState === 'granted') {
                                    this.hasDeviceOrientationPermission = true;
                                    this.$_addDeviceOrientationEventListener();
                                }
                            })
                            .catch(() => {
                                // Do not handle error, location marker can't be rotated
                            });
                    } else {
                        // Handle regular non iOS 13+ devices, permission is automatically granted
                        this.hasDeviceOrientationPermission = true;
                        this.$_addDeviceOrientationEventListener();
                    }
                }
            },

            /**
             * Is fired when fresh data is available from an orientation sensor about the current orientation of the device
             * @param event
             */
            $_handleDeviceOrientation(event) {
                // 0 degrees: natural orientation, which is mostly 'portrait-primary' for smartphones
                // 90 degrees: 'landscape-primary'
                // 180 degrees: 'portrait-secondary'
                // 270 degrees: 'landscape-secondary'
                const deviceOrientationAngle = window.screen.orientation.angle;

                let rotation;
                // Get value from several possible options to have best chance on getting an absolute direction reading
                if ('compassHeading' in event) {
                    rotation = event.compassHeading + deviceOrientationAngle;
                } else if ('webkitCompassHeading' in event) {
                    // iOS, rotation is reversed for iOS
                    rotation = -(
                        event.webkitCompassHeading + deviceOrientationAngle
                    );
                } else if (event.absolute) {
                    // Android
                    rotation = event.alpha + deviceOrientationAngle;
                }

                if (typeof rotation === 'number' && !Number.isNaN(rotation)) {
                    if (TRACK_OPTIONS[this.trackState].mapRotation) {
                        this.$_setMapRotation(DegreesToRadians(rotation));
                    }

                    if (TRACK_OPTIONS[this.trackState].markerRotation) {
                        this.$_setMarkerRotation(-DegreesToRadians(rotation));
                    }
                }
            },

            $_removeEventListeners() {
                OlUnByKey(this.errorHandler);
                OlUnByKey(this.changePositionHandler);
                OlUnByKey(this.pointerDragHandler);
                OlUnByKey(this.wheelHandler);

                window.removeEventListener(
                    'deviceorientation',
                    this.$_handleDeviceOrientation,
                    false
                );
            },

            $_geolocationClick() {
                if (!this.hasError) {
                    this.requestOrientationPermission();
                }
                this.$_toggleTrackState();
            },

            /**
             * Track location tri-state button.
             */
            $_toggleTrackState() {
                if (this.hasError) {
                    this.$_showMessage();
                    return;
                }

                // Next track tristate
                this.trackState = (this.trackState + 1) % 3;

                // Set state properties
                if (this.trackState === TRACK_STATES.INITIAL) {
                    // Show the map facing north
                    this.$_setMapRotation(0);
                    this.$_changePosition(true);
                } else if (this.trackState === TRACK_STATES.TRACK) {
                    this.$_changePosition(true);
                } else {
                    this.$_setMarkerRotation(0);
                    // Show message that map rotates
                    this.$store.commit('ADD_STATUS_MESSAGE', {
                        message: this.$t('mapRotates'),
                        variant: 'info',
                        autoHideDelay: 5000
                    });
                }
                // Publish track state properties
                this.gpsTracker?.setTracking(
                    TRACK_OPTIONS[this.trackState].markerTracking
                );
            },

            /**
             * Set map rotation in radians
             */
            $_setMapRotation(rotation) {
                this.$store.dispatch('updateViewPortMap', { rotation });
            },

            /**
             * Set marker rotation in radians
             */
            $_setMarkerRotation(rotation) {
                this.$store.commit('MARKER_GPS_UPDATE_ROTATION', {
                    mapName: 'main',
                    rotation
                });
            },

            /**
             * Set marker position
             */
            $_setMarkerPosition(point) {
                this.$store.commit('MARKER_GPS_UPDATE_GEOMETRY', {
                    mapName: 'main',
                    point
                });
            },

            /**
             * Change position
             */
            $_changePosition(geoButtonClicked) {
                // Set tracking on
                this.gpsTracker?.setTracking(
                    TRACK_OPTIONS[this.trackState].markerTracking
                );

                // Get the position
                const position = this.gpsTracker.getPosition();
                if (position) {
                    // Update position of location marker
                    let point = position.map((val) => parseFloat(val));
                    this.$_setMarkerPosition(point);

                    // Reset to center if geo button is clicked:
                    // - First click of the session we center and zoom in
                    // - After that we only center using the current zoom
                    if (geoButtonClicked) {
                        if (TRACK_OPTIONS[this.trackState].zoomToLocation) {
                            TRACK_OPTIONS[this.trackState].zoomToLocation = false;
                            this.setCenterAndZoomIn750(position);
                        } else {
                            this.setCenterAndCurrentZoom(position);
                        }
                    } else if (TRACK_OPTIONS[this.trackState].mapTracking) {
                        // Reset to center if mapTracking
                        this.setCenterAndCurrentZoom(position);
                    }
                }
            },

            /**
             * Stop map tracking, go to initial track state
             */
            $_gotoInitialTrackState() {
                this.trackState = TRACK_STATES.INITIAL;
            },

            /**
             * Show toast with error message
             */
            $_showMessage() {
                let gpsMessage = '';
                switch (this.errorCode) {
                    case ERROR_CODES.PERMISSION_DENIED:
                        gpsMessage = this.$t('gpsPermission');
                        break;
                    case ERROR_CODES.POSITION_UNAVAILABLE:
                        gpsMessage = this.$t('geomLocation');
                        break;
                    case ERROR_CODES.TIMEOUT:
                        // No message 'gpsTimeout' as it is not useful and pops up too often in the mobile application
                        break;
                }
                // Show message only once
                if (gpsMessage.length > 0 && !this.messageHasBeenShown) {
                    this.$store.commit('ADD_STATUS_MESSAGE', {
                        message: gpsMessage,
                        variant: 'info',
                        autoHideDelay: 5000
                    });
                    this.messageHasBeenShown = true;
                }
            },

            /**
             * Centers the position in the view
             * @param {object} center - the x and y position
             */
            setCenterAndCurrentZoom(center) {
                this.$store.dispatch('updateViewPortMap', { center });
            },

            /**
             * Centers the position in the view and zooms in to 1:750
             * @param {object} center - the x and y position
             */
            setCenterAndZoomIn750(center) {
                this.$store.dispatch('updateViewPortMap', { center });
                this.$store.dispatch('updateViewPortMap', {
                    zoom: ZOOM_LEVEL_SCALE_750
                });
            }
        }
    };
</script>

<style lang="scss" scoped>
.geo-location-link {
    display: flex;
    align-items: center;
}

.geobutton {
    display: inline-block;
    padding-top: 0px;
    background-color: #fff;
    border: 1px solid #b9b9b9;
    height: 30px;
    width: 30px;
    text-align: center;
    font-size: 16pt;
    line-height: normal;
    cursor: pointer;
}

.rotated_left {
    margin-top: 3px;
    margin-left: -1px;
    transform: rotate(-45deg);
}

.map_controls_position {
    width: 30px;
    height: 30px;
    display: block;
    background-repeat: no-repeat;
    background-position: center;
    background-size: 24px;
}

.map_controls_position.flex,
.map_controls_position.lost {
    background-image: url(~@/assets/icons/map_position_flex.svg);
}

.map_controls_position.fixed {
    background-image: url(~@/assets/icons/map_position_fixed.svg);
}

.map_controls_position.fixed_north {
    background-image: url(~@/assets/icons/map_position_fixed_north.svg);
}

.map_controls_position.rotated_left {
    margin-top: 3px;
    margin-left: -1px;
    transform: rotate(-45deg);
}

.map_controls_position.rotated_right {
    margin-top: -3px;
    transform: rotate(45deg);
}
</style>
